import { Injectable } from "@angular/core";
import { ContentValue } from "@fabric-types/svc-content";
import {
	CookieConsentConfigService,
	DataProtectionConfigService,
	FraudPreventionConfigService,
	GroupIBFraudPreventionService,
} from "@obg/ngx.common";
import { CoreHttpClient, CurrencyService, Market, ProductType } from "@obg/ngx.core";
import { DocumentRef } from "@obg/ngx.global.evo";
import { Observable } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";

import { RegistrationConfigService } from "../registration.config";
import { RegistrationResponse } from "../registration.model";
import { RegistrationService } from "../registration.service";

import { AamsRegistrationPayload, AamsRegistrationRequest } from "./aams-registration.model";

@Injectable({
	providedIn: "root",
})
export class AamsRegistrationService {
	constructor(
		private httpClient: CoreHttpClient,
		private registrationService: RegistrationService,
		private config: RegistrationConfigService,
		private fraudPreventionConfigService: FraudPreventionConfigService,
		private groupIBFraudPreventionService: GroupIBFraudPreventionService,
		private dataProtectionConfig: DataProtectionConfigService,
		private cookieConsentConfig: CookieConsentConfigService,
		private currencyService: CurrencyService,
		private documentRef: DocumentRef
	) {}

	register(payload: AamsRegistrationPayload, market: Market, product = ProductType.Common) {
		return new Observable(this.documentRef.obgEventBus.retrieve(ContentValue.getDefaultCountryCode)).pipe(
			take(1),
			map((countryCode) => this.buildRegisterRequest(market, payload, product, countryCode)),
			switchMap((requestData) =>
				this.httpClient.post<RegistrationResponse>({
					url: this.config.getRegisterEndpoint(),
					data: requestData,
				})
			)
		);
	}

	private buildRegisterRequest(
		market: Market,
		payload: AamsRegistrationPayload,
		registeredFromProduct: ProductType,
		countryCode: string
	): AamsRegistrationRequest {
		const { additionalDetails, iovationBlackBox, legal, credential } = payload;

		const { province, provinceCode, regionId, street, zipCode, city, phoneExtension, phoneNumber } =
			additionalDetails;

		let consents = payload.consents;
		let marketingOffers = payload.marketingOffers;

		if (!this.dataProtectionConfig.getMarketingConsentDisabled() && consents) {
			consents = {
				brandMarketing: consents.brandMarketing,
				...(this.dataProtectionConfig.getGroupMarketingDisabled() && {
					groupMarketing: false,
					thirdParty: true,
				}),
				...(this.cookieConsentConfig.getOneTrustIsEnabled() && { oneTrust: consents.oneTrust }),
			};

			marketingOffers = {
				acceptEmailOffers: consents?.brandMarketing,
				acceptSmsOffers: consents?.brandMarketing,
				acceptTelesalesOffers: consents?.brandMarketing,
			};
		}

		payload.person.firstName = this.replaceApostrophe(payload.person.firstName);
		payload.person.lastName = this.replaceApostrophe(payload.person.lastName);

		return {
			language: market.dataLanguageCode ?? market.languageCode,
			timeZone: "utc",
			address: {
				street,
				zipCode,
				city,
				country: countryCode,
				province,
				provinceCode,
				regionId,
			},
			affiliateMetadata: this.registrationService.getAffiliateMetaData(),
			credential,
			currency: this.currencyService.getCurrencyCodeByCountryCode(countryCode),
			depositLimit: payload.depositLimit,
			subscriptions: {
				...marketingOffers,
				email: credential.email,
				phoneExtension: phoneExtension.replace(/(^\+?)/, "+"),
				phoneNumber,
			},
			person: payload.person,
			iovationBlackBox: this.fraudPreventionConfigService.getEnabledInRegistration() ? iovationBlackBox : "",
			fraudToken: this.groupIBFraudPreventionService.getSessionId(),
			legal,
			productMetadata: {
				registeredFromProduct,
			},
			document: payload.document,
			recoveryQuestion: payload.recoveryQuestion,
		};
	}

	private replaceApostrophe(value: string): string {
		return value
			.replace("’", "'")
			.replace("ì", "i'")
			.replace("í", "i'")
			.replace("à", "a'")
			.replace("á", "a'")
			.replace("ò", "o'")
			.replace("ó", "o'")
			.replace("ù", "u'")
			.replace("ú", "u'")
			.replace("è", "e'")
			.replace("é", "e'");
	}
}
